/**
 * Centralized Logout Utility
 *
 * Single `performLogout()` function that:
 *   1. Sets a global logout-in-progress flag (prevents handleTokenRefreshFailure
 *      from overwriting the logout redirect with a login redirect)
 *   2. Unsubscribes from Pusher
 *   3. Removes the FCM token from the server (fire-and-forget)
 *   4. Clears all localStorage data
 *   5. Clears all Apollo client caches
 *   6. Redirects to the Keycloak logout endpoint
 *
 * Every logout trigger in the app should call `performLogout()`.
 */

import {
  client,
  clientV2,
  catClient,
  FRONT_END_SERVER_HOST,
  setLogoutInProgress,
} from "./index";
import { redirectToKeycloakLogout } from "./keycloakAuth";
import { REMOVE_FIREBASE_TOKEN } from "./graphqlQueries/queries";
import { unsubscribePusher } from "./pusher";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Fire-and-forget: tell the server to remove the stored FCM token.
 * Must be called BEFORE clearing localStorage so the Apollo clients
 * still have valid auth headers in their links.
 */
const removeFirebaseTokenFromServer = (): void => {
  try {
    const fcmToken = localStorage.getItem("fcm_token");
    if (fcmToken) {
      clientV2
        .mutate({ mutation: REMOVE_FIREBASE_TOKEN, variables: { token: fcmToken } })
        .catch(() => {});
      catClient
        .mutate({ mutation: REMOVE_FIREBASE_TOKEN, variables: { token: fcmToken } })
        .catch(() => {});
    }
  } catch {
    // Not critical for logout — swallow silently
  }
};

/**
 * Clear all three Apollo stores.
 * Uses `clearStore()` (not `resetStore()`) to avoid refetching active queries
 * which would trigger 401s after tokens are already removed.
 */
const clearApolloStores = (): void => {
  try {
    client.clearStore();
    clientV2.clearStore();
    catClient.clearStore();
  } catch {
    // Swallow — cache clear errors are not critical
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Perform a complete logout.
 *
 * @param continueUrl — URL Keycloak should redirect to after logout.
 *                       Defaults to the Login page.
 */
export const performLogout = (continueUrl?: string): void => {
  // 1. Set global flag so handleTokenRefreshFailure (triggered by async 401s
  //    from the fire-and-forget FCM mutation) does NOT overwrite our logout
  //    redirect with a login redirect.
  setLogoutInProgress(true);

  // 2. Unsubscribe from Pusher notifications
  try {
    unsubscribePusher();
  } catch {
    // non-critical
  }

  // 3. Remove FCM token from server (fire-and-forget, BEFORE clearing localStorage
  //    so auth headers are still valid)
  try {
    removeFirebaseTokenFromServer();
  } catch {
    // non-critical
  }

  // 4. Clear all localStorage
  try {
    localStorage.clear();
  } catch {
    // non-critical
  }

  // 5. Clear Apollo caches
  try {
    clearApolloStores();
  } catch {
    // non-critical
  }

  // 6. Redirect to Keycloak logout endpoint
  const redirectUrl = continueUrl || `${FRONT_END_SERVER_HOST}Login`;
  redirectToKeycloakLogout(redirectUrl);
};

export default { performLogout };
